import { PlusOutlined, PrinterOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Dropdown, Flex, Form, InputNumber, Modal, Select, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { CLIENT_PAGING } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { NoWms, useWmsWarehouse } from "./common";
import { printPdf } from "./packLabel";
import { usePoll } from "@/components/tabs/WorkTabs";

type Container = Schemas["Container"];
type Size = "100x150" | "100x100" | "100x50";
const SIZES: Size[] = ["100x150", "100x100", "100x50"];

/**
 * Gudang > Pallet (Plan §6.9): license plates for goods received together.
 * Labels print ahead in a batch (stuck on at receiving) or again later with
 * what the pallet holds; each pallet's putaway is found by scanning it.
 */
export function Pallets() {
  const { t, formatNumber } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const { current, loading } = useWmsWarehouse();
  const [selected, setSelected] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [printing, setPrinting] = useState(false);
  const list = useQuery({
    queryKey: ["containers", current?.id, "pallet"],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/containers", { params: { path: { warehouseID: current!.id }, query: { kind: "pallet" } } })),
    enabled: !!current,
    refetchInterval: usePoll(15_000),
  });
  const print = async (ids: string[], size?: Size) => {
    setPrinting(true);
    try {
      printPdf(
        await unwrap(
          api.GET("/v1/warehouses/{warehouseID}/container-labels", {
            params: { path: { warehouseID: current!.id }, query: { ids: ids.join(","), ...(size ? { size } : {}) } },
            parseAs: "blob",
          }),
        ),
      );
    } catch (err) {
      void message.error(errorText(err));
    } finally {
      setPrinting(false);
    }
  };

  if (!loading && !current) return <NoWms />;
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("wms.plt.title")}
        </Typography.Title>
        <Flex gap={8} wrap>
          <Dropdown
            disabled={selected.length === 0}
            menu={{ items: SIZES.map((s) => ({ key: s, label: `${s} mm` })), onClick: ({ key }) => void print(selected, key as Size) }}
          >
            <Button icon={<PrinterOutlined />} loading={printing}>
              {t("wms.ct.print", { n: selected.length })}
            </Button>
          </Dropdown>
          {can("warehouse.manage") && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
              {t("wms.plt.add")}
            </Button>
          )}
        </Flex>
      </Flex>
      <Typography.Paragraph type="secondary">{t(current?.palletReceiving ? "wms.plt.helpOn" : "wms.plt.helpOff")}</Typography.Paragraph>
      <Table<Container>
        rowKey="id"
        loading={list.isPending}
        dataSource={list.data?.items ?? []}
        pagination={CLIENT_PAGING}
        rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys.map(String)) }}
        locale={{ emptyText: t("wms.plt.empty") }}
        columns={[
          { title: t("wms.ct.code"), dataIndex: "code", width: 170, render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
          {
            title: t("common.status"),
            render: (_, c) =>
              c.taskId ? (
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(`/app/warehouse/tasks/${c.taskId}`)}>
                  {t("wms.plt.waiting", { task: c.taskNumber })}
                </Button>
              ) : c.units > 0 ? (
                <Tag color="gold">{t("wms.ct.holding")}</Tag>
              ) : (
                <Tag color="success">{t("wms.ct.free")}</Tag>
              ),
          },
          { title: t("wms.ct.units"), dataIndex: "units", width: 110, align: "right", render: (n: number) => <span className="z-num">{formatNumber(n)}</span> },
        ]}
      />
      {current && adding && (
        <AddPallets
          warehouseId={current.id}
          size={(current.palletLabel as Size) ?? "100x150"}
          onClose={() => setAdding(false)}
          onMade={(ids, size) => void print(ids, size)}
        />
      )}
    </>
  );
}

/** "Cetak label baru": make pallets and print their labels at once. */
function AddPallets({ warehouseId, size, onClose, onMade }: { warehouseId: string; size: Size; onClose: () => void; onMade: (ids: string[], size: Size) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<{ count: number; size: Size }>();
  const add = useMutation({
    mutationFn: (v: { count: number; size: Size }) =>
      unwrap(api.POST("/v1/warehouses/{warehouseID}/containers", { params: { path: { warehouseID: warehouseId } }, body: { kind: "pallet", count: v.count } })).then(
        (r) => ({ r, size: v.size }),
      ),
    onSuccess: ({ r, size }) => {
      void message.success(t("wms.plt.added", { n: r.codes.length, first: r.codes[0] ?? "", last: r.codes[r.codes.length - 1] ?? "" }));
      void queryClient.invalidateQueries({ queryKey: ["containers"] });
      onMade(r.ids, size);
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal open title={t("wms.plt.add")} okText={t("wms.plt.addOk")} cancelText={t("common.cancel")} confirmLoading={add.isPending} onOk={() => form.submit()} onCancel={onClose} destroyOnHidden>
      <Form form={form} layout="vertical" initialValues={{ count: 20, size }} onFinish={(v) => add.mutate(v)}>
        <Form.Item name="count" label={t("wms.ct.count")} extra={t("wms.plt.countHelp")}>
          <InputNumber min={1} max={200} precision={0} style={{ width: 160 }} />
        </Form.Item>
        <Form.Item name="size" label={t("wms.plt.size")}>
          <Select options={SIZES.map((s) => ({ value: s, label: `${s} mm` }))} style={{ width: 200 }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
